import { ElementType, ReactNode, useEffect, useRef, useState } from 'react'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ChevronBoldRightIcon from '~/components/Icons/svgs/ChevronBoldRightIcon'
interface CollapseProps {
  title: string | React.ReactElement
  children?: ReactNode
  className?: string // Wrapper css class
  titleClassName?: string // Button css class
  collapseDirection?: 'top' | 'bottom'
  arrowDirection?: 'left' | 'right'
  initiallyExpanded?: boolean
  containerClassName?: string
  arrowClassName?: string
  onChange?: (isExpanded: boolean) => void
  toggleOnChildrenClick?: boolean // Toggle collapse on children click
  isDisabled?: boolean
  onTitleClick?: (isCollapsed: boolean) => void // Fired when clicked on title
  arrowIconComponent?: ElementType
  arrowSize?: number
  arrowRotation?: string // CSS Class for arrow rotation
  arrowRotationCollapsed?: string // CSS Class for arrow rotation when collapsed
}

const Collapse = ({
  title,
  children,
  className = '',
  titleClassName = '',
  collapseDirection = 'bottom',
  arrowDirection = 'right',
  initiallyExpanded = false,
  containerClassName = '',
  onChange,
  toggleOnChildrenClick,
  isDisabled = false,
  onTitleClick,
  arrowIconComponent = ChevronBoldRightIcon,
  arrowClassName = 'text-white',
  arrowSize = 16,
  arrowRotation = 'rotate-90',
  arrowRotationCollapsed = 'rotate-0',
}: CollapseProps): React.ReactElement => {
  const [height, setHeight] = useState<number>(0)
  const [expanded, setExpanded] = useState(initiallyExpanded)
  const contentRef = useRef<HTMLDivElement>(null)
  const contentRect = contentRef?.current?.getBoundingClientRect()

  useEffect(() => {
    if (contentRect?.height) {
      setHeight(contentRect.height)
    }
  }, [contentRect])

  useEffect(() => {
    setExpanded(initiallyExpanded)
  }, [initiallyExpanded])

  useEffect(() => {
    if (onChange) onChange(expanded)
  }, [expanded, onChange])

  const handleClick = (): void => {
    if (!isDisabled) {
      setExpanded((ex) => {
        if (onTitleClick) onTitleClick(!ex)

        return !ex
      })
    }
  }

  const childrenClickHandler = (): void => {
    if (toggleOnChildrenClick) handleClick()
  }

  const body = (
    <div
      className={`overflow-hidden ${containerClassName}`}
      style={{
        height: expanded ? height : 0,
        transition: `height 200ms cubic-bezier(0.4, 0, 1, 1)`,
      }}
    >
      <div onClick={childrenClickHandler} ref={contentRef}>
        {children}
      </div>
    </div>
  )

  const arrowIcon = (
    <span
      className={`transform ${expanded ? arrowRotation : arrowRotationCollapsed}`}
      style={{ transition: 'transform 200ms cubic-bezier(0.4, 0, 1, 1)' }}
    >
      <SVGIcon icon={arrowIconComponent} pathClassName={arrowClassName} size={arrowSize} />
    </span>
  )

  const toggleButton = (
    <button
      className={`p-1 flex items-center disabled:opacity-20 disabled:cursor-not-allowed ${titleClassName}`}
      disabled={isDisabled}
      onClick={handleClick}
    >
      {arrowDirection === 'left' && arrowIcon}
      <span className="mx-0.5">{title}</span>
      {arrowDirection === 'right' && arrowIcon}
    </button>
  )

  return (
    <div className={className}>
      {collapseDirection === 'top' && body}
      {toggleButton}
      {collapseDirection === 'bottom' && body}
    </div>
  )
}

export default Collapse
